import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { MarkdownEditor } from 'Components/ProductExamples/MarkdownEditor';
import { MarkdownRenderer } from 'Components/MarkdownRenderer';
import { FreiePosition } from 'apiClient/Api';
import { Rubric } from './calculatorApi';

/** Die drei preisneutralen Angebotstexte. */
export interface OfferTextsState {
    vorbemerkung: string;
    projektbeschreibung: string;
    zusammenfassung: string;
}

/** Ein leerer Textsatz — Startwert für ein neues Projekt. */
export const emptyOfferTexts = (): OfferTextsState => ({
    vorbemerkung: '',
    projektbeschreibung: '',
    zusammenfassung: '',
});

interface OfferExtrasPanelProps {
    texts: OfferTextsState;
    onTextsChange: (texts: OfferTextsState) => void;
    positions: FreiePosition[];
    onPositionsChange: (positions: FreiePosition[]) => void;
    rubrics: Rubric[];
    /** Preise ausblenden (Aufrufer ohne Preisrecht). */
    hidePrices?: boolean;
    disabled?: boolean;
}

/** Gängige Einheiten als Schnellauswahl; das Feld bleibt trotzdem frei beschreibbar. */
const UNITS = ['Stk', 'Std', 'psch', 'Tag', 'km', 'm'];

/** Eine neue, leere Position. Rubrik I = Inbetriebnahme/Dienstleistung — dort gehören Sonderleistungen meist hin. */
const newPosition = (): FreiePosition => ({
    bezeichnung: '',
    menge: 1,
    einheit: 'Stk',
    einzelpreis: 0,
    rubrik: 'I',
    istOption: false,
    beschreibung: null,
    area: '',
});

const money = (v: number) =>
    v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

/**
 * Eingabe der Angebotstexte (Markdown) und der freien Positionen — also alles, was nicht aus dem
 * Katalog kommt.
 *
 * Zusammengefasst in einem einklappbaren Block, weil der Normalfall „nichts davon" ist: die
 * Anforderungsauswahl darunter soll nicht durch drei Editoren nach unten geschoben werden, die
 * meistens leer bleiben. Ein Zähler im Kopf zeigt, ob etwas drinsteht.
 */
export const OfferExtrasPanel: React.FC<OfferExtrasPanelProps> = ({
    texts,
    onTextsChange,
    positions,
    onPositionsChange,
    rubrics,
    hidePrices,
    disabled,
}) => {
    const { t } = useTranslation();
    const [preview, setPreview] = useState(false);

    const filled = [texts.vorbemerkung, texts.projektbeschreibung, texts.zusammenfassung]
        .filter((s) => s.trim().length > 0).length;

    const setText = (key: keyof OfferTextsState) => (value: string) =>
        onTextsChange({ ...texts, [key]: value });

    const updatePosition = (idx: number, patch: Partial<FreiePosition>) =>
        onPositionsChange(positions.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

    const removePosition = (idx: number) =>
        onPositionsChange(positions.filter((_, i) => i !== idx));

    // Optionale Positionen zählen nicht mit — genau wie serverseitig in ProjectExtras.
    const total = positions
        .filter((p) => !p.istOption)
        .reduce((sum, p) => sum + (p.menge ?? 0) * (p.einzelpreis ?? 0), 0);

    const textField = (key: keyof OfferTextsState, labelKey: string, hintKey: string) => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                {t(labelKey)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t(hintKey)}
            </Typography>
            {preview ? (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, minHeight: 80 }}>
                    {texts[key].trim()
                        ? <MarkdownRenderer>{texts[key]}</MarkdownRenderer>
                        : <Typography variant="body2" color="text.secondary">{t('calculation.extras.empty')}</Typography>}
                </Box>
            ) : (
                <MarkdownEditor
                    value={texts[key]}
                    onChange={setText(key)}
                    disabled={disabled}
                    placeholder={t(hintKey)}
                />
            )}
        </Box>
    );

    return (
        <Accordion defaultExpanded={filled > 0 || positions.length > 0} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Typography variant="h6">{t('calculation.extras.title')}</Typography>
                    {filled > 0 && (
                        <Chip size="small" color="primary" variant="outlined"
                              label={t('calculation.extras.textCount', { count: filled })} />
                    )}
                    {positions.length > 0 && (
                        <Chip size="small" color="primary" variant="outlined"
                              label={t('calculation.extras.positionCount', { count: positions.length })} />
                    )}
                </Stack>
            </AccordionSummary>

            <AccordionDetails>
                <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 1 }}>
                    <Button size="small" onClick={() => setPreview((p) => !p)}>
                        {preview ? t('calculation.extras.edit') : t('calculation.extras.preview')}
                    </Button>
                </Stack>

                {textField('vorbemerkung', 'calculation.extras.vorbemerkung', 'calculation.extras.vorbemerkungHint')}
                {textField('projektbeschreibung', 'calculation.extras.beschreibung', 'calculation.extras.beschreibungHint')}
                {textField('zusammenfassung', 'calculation.extras.zusammenfassung', 'calculation.extras.zusammenfassungHint')}

                <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>
                    {t('calculation.extras.positions')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('calculation.extras.positionsHint')}
                </Typography>

                {positions.length > 0 && (
                    <Table size="small" sx={{ mb: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('calculation.extras.label')}</TableCell>
                                <TableCell width={90}>{t('calculation.extras.quantity')}</TableCell>
                                <TableCell width={110}>{t('calculation.extras.unit')}</TableCell>
                                {!hidePrices && <TableCell width={130}>{t('calculation.extras.unitPrice')}</TableCell>}
                                <TableCell width={150}>{t('calculation.extras.rubric')}</TableCell>
                                <TableCell width={110}>{t('calculation.extras.optional')}</TableCell>
                                {!hidePrices && <TableCell width={120} align="right">{t('calculation.extras.total')}</TableCell>}
                                <TableCell width={50} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {positions.map((p, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <TextField
                                            fullWidth size="small" variant="standard" disabled={disabled}
                                            value={p.bezeichnung ?? ''}
                                            placeholder={t('calculation.extras.labelPlaceholder')}
                                            onChange={(e) => updatePosition(idx, { bezeichnung: e.target.value })}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number" size="small" variant="standard" disabled={disabled}
                                            value={p.menge ?? 0}
                                            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
                                            onChange={(e) => updatePosition(idx, { menge: Number(e.target.value) })}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            select size="small" variant="standard" fullWidth disabled={disabled}
                                            value={UNITS.includes(p.einheit ?? '') ? p.einheit : UNITS[0]}
                                            onChange={(e) => updatePosition(idx, { einheit: e.target.value })}
                                        >
                                            {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                                        </TextField>
                                    </TableCell>
                                    {!hidePrices && (
                                        <TableCell>
                                            <TextField
                                                type="number" size="small" variant="standard" disabled={disabled}
                                                value={p.einzelpreis ?? 0}
                                                slotProps={{ htmlInput: { step: '0.01' } }}
                                                onChange={(e) => updatePosition(idx, { einzelpreis: Number(e.target.value) })}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <TextField
                                            select size="small" variant="standard" fullWidth disabled={disabled}
                                            value={p.rubrik ?? 'I'}
                                            onChange={(e) => updatePosition(idx, { rubrik: e.target.value })}
                                        >
                                            {rubrics.map((r) => (
                                                <MenuItem key={r.code} value={r.code ?? ''}>{r.label}</MenuItem>
                                            ))}
                                        </TextField>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={t('calculation.extras.optionalHint') ?? ''}>
                                            <TextField
                                                select size="small" variant="standard" fullWidth disabled={disabled}
                                                value={p.istOption ? '1' : '0'}
                                                onChange={(e) => updatePosition(idx, { istOption: e.target.value === '1' })}
                                            >
                                                <MenuItem value="0">{t('no')}</MenuItem>
                                                <MenuItem value="1">{t('yes')}</MenuItem>
                                            </TextField>
                                        </Tooltip>
                                    </TableCell>
                                    {!hidePrices && (
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                color={p.istOption ? 'text.secondary' : 'text.primary'}
                                            >
                                                {money((p.menge ?? 0) * (p.einzelpreis ?? 0))}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <IconButton
                                            size="small" disabled={disabled}
                                            onClick={() => removePosition(idx)}
                                            aria-label={t('calculation.extras.remove') ?? 'remove'}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
                    <Button
                        size="small" variant="outlined" disabled={disabled}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => onPositionsChange([...positions, newPosition()])}
                    >
                        {t('calculation.extras.add')}
                    </Button>
                    {!hidePrices && positions.length > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {t('calculation.extras.sum', { value: money(total) })}
                        </Typography>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

export default OfferExtrasPanel;
